import { useCoreApi } from '@/composables/useApi'
import { IWorkshop } from '@lifk/types'
import { AxiosInstance } from 'axios'
import { Socket } from 'socket.io-client'
import useSWRV from 'swrv'
import { inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

interface GetWorkshopSWRVKey {
  name: string
  workshopUuid?: string
}

async function fetchWorkshop(workshopUuid: string, api: AxiosInstance) {
  const response = await api.get(`workshops/${workshopUuid}`)
  if (response.data.error) {
    throw response.data.error
  }
  return response.data.data as IWorkshop
}

/**
 * Hook returning a SWRV result to get the workshop for the current route
 * Is updated automatically when the route changes
 */
// let i = 0
const useGetWorkshopCurrentRoute = () => {
  const api = useCoreApi()
  const route = useRoute()

  const socket = inject<Socket>('socket')
  const currentRouteSWRVKey = ref({
    name: 'workshop',
    workshopUuid: route.params.uuid
  } as GetWorkshopSWRVKey)

  const result = useSWRV(
    () =>
      `/${currentRouteSWRVKey.value.name}/${currentRouteSWRVKey.value.workshopUuid}`,
    async () => {
      if (currentRouteSWRVKey.value.workshopUuid) {
        return await fetchWorkshop(currentRouteSWRVKey.value.workshopUuid, api)
      } else {
        return null
      }
    },
    {
      dedupingInterval: 100,
      shouldRetryOnError: false
    }
  )

  watch(
    () => route.params?.uuid,
    () => {
      currentRouteSWRVKey.value.workshopUuid = route.params?.uuid
        ? route.params.uuid.toString()
        : undefined
    }
  )

  const onWorkshopUpdated = (workshopUuid: string) => {
    if (currentRouteSWRVKey.value.workshopUuid === workshopUuid) {
      result.mutate()
    }
  }

  onMounted(() => {
    socket?.on('workshop-updated', onWorkshopUpdated)
  })
  onUnmounted(() => {
    socket?.off('workshop-updated', onWorkshopUpdated)
  })

  return result
}

export { fetchWorkshop, useGetWorkshopCurrentRoute }
